import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {BatchSchema} from 'constants/SampleConstants';
import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {Schema} from 'schemas/SchemaConstants';
import {FilterOption} from 'search/filters/base/FilterComponents';
import {FilterGroup} from 'search/filters/base/FilterFormConstants';
import {getFilterKeysForModelType} from 'search/FiltersByType';
import {FilterKey} from 'search/SearchConstants';
import {assertNever} from 'util/Types';

import {
  GENERAL_FILTER_OPTIONS_WITHOUT_SCHEMA_FIELD,
  PROCESS_FILTER_OPTIONS,
  PROCESS_TASK_FILTER_OPTIONS,
  PROCESS_OUTPUT_FILTER_OPTIONS,
  PROCESS_FLOWCHART_FILTER_OPTIONS,
  ANY_LINK_FILTER,
} from './ProcessFilterConstants';

type SecondaryFilterReturnType = (selectedSchema: Schema | BatchSchema | null) => Array<FilterGroup>;

function getSecondaryFiltersForProcesses(): SecondaryFilterReturnType {
  return (selectedSchema: Schema | BatchSchema | null) => [
    {
      name: 'General',
      filterOptions: compact([
        ...GENERAL_FILTER_OPTIONS_WITHOUT_SCHEMA_FIELD,
        ANY_LINK_FILTER,
        BenchlingConfig.STUDIES_ENABLE ? {filterKey: FilterKey.IN_STUDY} : null,
        selectedSchema != null
          ? {
              filterKey: FilterKey.SCHEMA_FIELD,
              extraProps: {
                schemaFields: selectedSchema.fields,
              },
            }
          : null,
      ]),
    },
    {
      name: 'Workflow task group',
      filterOptions: [
        ...PROCESS_FILTER_OPTIONS,
        ...(BenchlingConfig.REQUESTS_V2_ENABLE
          ? [{filterKey: FilterKey.REQUEST_V2_SUBMISSION} as FilterOption]
          : []),
      ],
    },
    {
      name: 'Workflow task',
      filterOptions: PROCESS_TASK_FILTER_OPTIONS,
    },
    {
      name: 'Workflow output',
      filterOptions: PROCESS_OUTPUT_FILTER_OPTIONS,
    },
    {
      name: 'Flowchart',
      filterOptions: PROCESS_FLOWCHART_FILTER_OPTIONS,
    },
  ];
}

function getSecondaryFiltersForRecipes(): SecondaryFilterReturnType {
  return (selectedSchema: Schema | BatchSchema | null) => [
    {
      name: 'General',
      filterOptions: compact([
        ...GENERAL_FILTER_OPTIONS_WITHOUT_SCHEMA_FIELD,
        ANY_LINK_FILTER,
        BenchlingConfig.STUDIES_ENABLE ? {filterKey: FilterKey.IN_STUDY} : null,
      ]),
    },
    {
      name: 'Workflow task',
      filterOptions: [
        {filterKey: FilterKey.PROCEDURE_TASK_STATUS},
        {filterKey: FilterKey.PROCESS_TASK_ASSIGNEE},
        {filterKey: FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE},
      ],
    },
    {
      name: processExecutionTypeDisplayName(ProcessClientOrigin.RECIPES, ProcessExecutionType.PROCEDURE),
      filterOptions: [{filterKey: FilterKey.RECIPE_TEMPLATE}],
    },
    {
      name: processExecutionTypeDisplayName(
        ProcessClientOrigin.RECIPES,
        ProcessExecutionType.PROCEDURE_METHOD
      ),
      filterOptions: [
        {filterKey: FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS},
        {filterKey: FilterKey.RECIPE_RUN_CONDITION_NAMES},
        {filterKey: FilterKey.RECIPE_RUN_REPLICATE_NAMES},
      ],
    },
    {
      name: processExecutionTypeDisplayName(ProcessClientOrigin.RECIPES, ProcessExecutionType.PROCEDURE_STEP),
      filterOptions: [{filterKey: FilterKey.RECIPE_METHOD_NAME}],
    },
  ];
}

function getSecondaryFiltersForAssays(): SecondaryFilterReturnType {
  return (selectedSchema: Schema | BatchSchema | null) => [
    {
      name: 'General',
      filterOptions: compact([
        ...GENERAL_FILTER_OPTIONS_WITHOUT_SCHEMA_FIELD,
        ANY_LINK_FILTER,
        BenchlingConfig.STUDIES_ENABLE ? {filterKey: FilterKey.IN_STUDY} : null,
      ]),
    },
    {
      name: 'Workflow task',
      filterOptions: [
        {filterKey: FilterKey.PROCEDURE_TASK_STATUS},
        {filterKey: FilterKey.PROCESS_TASK_ASSIGNEE},
        {filterKey: FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE},
      ],
    },
    {
      name: processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE),
      filterOptions: [{filterKey: FilterKey.ASSAY_TEMPLATE}],
    },
    {
      name: processExecutionTypeDisplayName(
        ProcessClientOrigin.ASSAYS,
        ProcessExecutionType.PROCEDURE_METHOD
      ),
      filterOptions: [
        {filterKey: FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS},
        {filterKey: FilterKey.ASSAY_RUN_CONDITION_NAMES},
        {filterKey: FilterKey.ASSAY_RUN_REPLICATE_NAMES},
      ],
    },
    {
      name: processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE_STEP),
      filterOptions: [{filterKey: FilterKey.ASSAY_METHOD_NAME}],
    },
  ];
}

export function getAvailablePrimaryFilters(
  selectedClientOrigin: ProcessClientOrigin,
  selectedTypes: Array<string>
): Array<FilterOption> {
  switch (selectedClientOrigin) {
    case ProcessClientOrigin.PROCESSES:
    case ProcessClientOrigin.DISCREPANCIES:
      return selectedTypes
        .map((selectedType) => getFilterKeysForModelType(selectedType))
        .flat()
        .includes(FilterKey.ANCESTOR_FOLDER)
        ? [{filterKey: FilterKey.ANCESTOR_FOLDER}]
        : [];
    case ProcessClientOrigin.RECIPES:
    case ProcessClientOrigin.ASSAYS:
      return [];
    default:
      assert(false, `Unexpected value for client origin '${assertNever(selectedClientOrigin)}'`);
      return [];
  }
}

export function getAvailableSecondaryFilters(
  selectedClientOrigin: ProcessClientOrigin
): SecondaryFilterReturnType {
  switch (selectedClientOrigin) {
    case ProcessClientOrigin.PROCESSES:
    case ProcessClientOrigin.DISCREPANCIES:
      return getSecondaryFiltersForProcesses();
    case ProcessClientOrigin.RECIPES:
      return getSecondaryFiltersForRecipes();
    case ProcessClientOrigin.ASSAYS:
      return getSecondaryFiltersForAssays();
    default:
      assert(false, `Unexpected value for client origin '${assertNever(selectedClientOrigin)}'`);
      return () => [];
  }
}
